<template>
  <Modal
    v-model:visible="isVisible"
    class="location-edit-modal"
    :class="[addressBookType]"
    size="md"
    @keypress.esc="onCancel"
    @keypress.enter="onSave"
  >
    <template #header>
      <div class="location-edit-modal-header">
        <div class="location-edit-modal-header-label">
          <PickupIcon v-if="isPickup" />
          <DropoffIcon v-else />
          <span class="p-dialog-title" v-text="header" />
        </div>

        <div class="location-edit-modal-header-address">
          <div class="header-left">
            <span class="location-name">
              {{
                internalValue.id
                  ? internalValue.location?.locationName
                  : t('common.newParam', { param: t('locations.locations') })
              }}
            </span>

            <RygenButton
              v-if="editable"
              mode="tertiary"
              :label="
                internalValue.id
                  ? t('common.changes')
                  : t('address.addressBook')
              "
              @click="onShowAddressBook"
            />
          </div>

          <RygenButton
            v-if="editable && (internalValue.id || v$.$anyDirty)"
            mode="tertiary"
            :label="t('orders.clearAndStartOver')"
            @click="onClear"
          />
        </div>
      </div>
    </template>

    <template #default>
      <div class="location-edit-modal-body flex flex-column flex-gap-1">
        <ShippingPointLocationEdit
          :id="`${id}-location`"
          ref="locationEditRef"
          v-model="internalValue.location"
          :disabled="!editable"
          append-to="body"
          allow-ficon-updates
          @address-book-select="onLocationSelect"
          @autocomplete="onAutocompleteSelected"
          @saving="savingLocationGroup = $event"
        >
          <template
            v-if="showUpdateAddressBookSwitch"
            #extra-input="{ disabled }"
          >
            <WrappedSwitch
              :id="`${id}-update-address-book`"
              v-model="updateAddressBook"
              :label="t('shippingPoints.updateAddressBook')"
              :disabled="disabled"
              class="w-3"
            />
          </template>
        </ShippingPointLocationEdit>
      </div>

      <AddressBookModal
        v-model:visible="isShowingAddressBookModal"
        v-model:search-criteria="searchCriteria"
        :header="t('common.editParam', { param: header })"
        :search-results="searchResults"
        :type="addressBookType"
        :data-testid="`location-edit-modal-address-book-modal-${internalValue.sequenceNumber}`"
        @select="onLocationSelect"
      />

      <AddressValidationResultModal
        v-model:visible="isShowingAddressValidationModal"
        :class="{ origin: isPickup, destination: !isPickup }"
        :validation-result="validationResult.data"
        @accept-corrected-address="onAcceptCorrection"
        @keep-original-address="onKeepOriginalAddress"
      />
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :disabled="validationResult.loading"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="editable"
        mode="primary"
        icon="pi pi-save"
        :disabled="validationResult.loading || savingLocationGroup"
        :label="
          t('common.saveParam', {
            param: isPickup ? t('orders.origin') : t('orders.destination'),
          })
        "
        :loading="validationResult.loading || savingLocationGroup"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useValidations } from '@rygen/rygen-ui/composables';
import {
  AddressValidationResult,
  Loadable,
  Location,
  ShippingPointDetails,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShippingPoints } from '@/composables';
import i18n from '@/i18n';
import {
  DoesUserHavePermissionEntityType,
  EmbeddableContact,
  EmbeddableLocation,
  OrderShippingPoint,
  ShipmentStop,
  ShipmentStopType,
  ShippingPoint,
} from '@/model';

import ShippingPointLocationEdit from '@/components/order/ShippingPointLocationEdit.vue';
import DropoffIcon from '@/components/shippingPoint/DropoffIcon.vue';
import PickupIcon from '@/components/shippingPoint/PickupIcon.vue';
import AddressBookModal from '@rygen/rygen-ui/components/address/AddressBookModal.vue';
import AddressValidationResultModal from '@rygen/rygen-ui/components/address/AddressValidationResultModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

export type LocationEditPayload = {
  location: ShippingPoint;
  updateAddressBook: boolean;
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  location: {
    type: Object as PropType<ShippingPoint>,
    default: () => new OrderShippingPoint(),
  },
  header: {
    type: String,
    default: () => i18n.global.t('orders.origin'),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, location, header, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', payload: LocationEditPayload): void;
}>();

const locationEditRef = useTemplateRef('locationEditRef');

const internalValue: Ref<ShippingPoint> = ref(new OrderShippingPoint());

const { t } = useI18n();
const { resetSearch, searchCriteria, searchResults } = useShippingPoints();
const toast = useToast();
const { v$, validate } = useValidations(internalValue, {}, id.value);

const shouldValidate = ref(true);
const validationResult: Ref<Loadable<AddressValidationResult>> = ref({
  loading: false,
});
const isShowingAddressBookModal = ref(false);
const isShowingAddressValidationModal = ref(false);
const updateAddressBook = ref(false);
const doesUserHaveUpdateLocationPermission = ref(false);
const savingLocationGroup = ref(false);

const isPickup = computed(
  () => internalValue.value.stopType === ShipmentStopType.PICKUP,
);

const addressBookType = computed(() => (isPickup.value ? 'pickup' : 'dropoff'));

const isFiconLocation = computed(
  () => !!internalValue.value.location.locationId,
);

const showUpdateAddressBookSwitch = computed(() => {
  const shippingPoint = internalValue.value;
  const isOrderOrShipment =
    shippingPoint instanceof OrderShippingPoint ||
    shippingPoint instanceof ShipmentStop;

  return (
    isFiconLocation.value &&
    isOrderOrShipment &&
    doesUserHaveUpdateLocationPermission.value
  );
});

const onCancel = () => {
  const Clazz = Object.getPrototypeOf(location.value).constructor;
  internalValue.value = new Clazz(location.value);
  emit('cancel');
  isVisible.value = false;
};

const onClear = () => {
  const Clazz = Object.getPrototypeOf(location.value).constructor;
  internalValue.value = new Clazz({
    id: internalValue.value?.id,
    location: new EmbeddableLocation(),
    contact: new EmbeddableContact(),
    sequenceNumber: location.value?.sequenceNumber,
    stopType: location.value?.stopType,
  });
  nextTick(() => v$.value.$reset());
};

const validateLocation = async (): Promise<boolean> => {
  try {
    validationResult.value.loading = true;
    if (!internalValue.value.location || !shouldValidate.value) {
      return true;
    }

    const address = internalValue.value.location.toAddress();
    validationResult.value.data =
      await api.shippingPoint.validateAddress(address);

    if (validationResult.value.data.errors?.length) {
      isShowingAddressValidationModal.value = true;
      return false;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    console.error('Error validating address', e);
  } finally {
    validationResult.value.loading = false;
  }

  return true;
};

/**
 * Save the current internal location
 *
 * This method will fire the events to save the internal location to the parent
 * order/shipment, so it should only be called after the internal location has
 * been fully validated.
 */
const saveValidatedLocation = () => {
  emit('save', {
    location: internalValue.value,
    updateAddressBook: updateAddressBook.value,
  });
  isVisible.value = false;
};

const onSave = async () => {
  if (!editable.value) {
    onCancel();
    return;
  }

  if ((await validate()) && (await validateLocation())) {
    saveValidatedLocation();
  }
};

const onAutocompleteSelected = (isAutocompleteSelection: boolean) => {
  shouldValidate.value = !isAutocompleteSelection;
};

const onShowAddressBook = () => {
  locationEditRef.value?.cancelExistingAutocompleteRequest();
  isShowingAddressBookModal.value = true;
};

const onAcceptCorrection = async () => {
  internalValue.value.location = EmbeddableLocation.fromAddress(
    validationResult.value.data?.correctedAddress?.address,
    internalValue.value.location?.locationName,
  );
  isShowingAddressValidationModal.value = false;
  if (await validate()) {
    saveValidatedLocation();
  }
};

const onKeepOriginalAddress = async () => {
  internalValue.value.location = EmbeddableLocation.fromAddress(
    validationResult.value.data?.originalAddress,
    internalValue.value.location?.locationName,
  );
  isShowingAddressValidationModal.value = false;
  if (await validate()) {
    saveValidatedLocation();
  }
};

const setDefaultAccessorials = () => {
  const defaultAccessorials =
    internalValue.value.shippingPointDetails?.defaultAccessorials ?? [];
  const existingAccessorials = internalValue.value.requestedAccessorials.filter(
    (existingAcc) => {
      const isDefault = defaultAccessorials.some(
        (defaultAcc) => defaultAcc.naturalId === existingAcc.naturalId,
      );
      return !isDefault;
    },
  );

  internalValue.value.requestedAccessorials = [
    ...defaultAccessorials,
    ...existingAccessorials,
  ];
};

const onLocationSelect = async (location: Location) => {
  if (!location.id) {
    return;
  }

  try {
    internalValue.value.location = EmbeddableLocation.fromLocation(location);
    internalValue.value.shippingPointDetails = new ShippingPointDetails(
      location.shippingPointDetails,
    );
    setDefaultAccessorials();
    internalValue.value.contact =
      await api.shippingPoint.findPrimaryContactForShippingPoint(location.id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No primary contact is fine
      return;
    }
    console.error('Error loading primary contact for shipping point', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('contacts.contacts'),
      }),
      error: e,
    });
  }
};

const loadDoesUserHavePermission = async (ficonLocationId: number) => {
  try {
    doesUserHaveUpdateLocationPermission.value =
      await api.user.doesCurrentUserHavePermission(
        DoesUserHavePermissionEntityType.LOCATION,
        ficonLocationId,
        'location:update',
        true,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading user permission', e);
  }
};

watch(
  internalValue,
  (newVal) => {
    // We only care about adhoc locations, not saved locations
    const isAdhocLocation =
      !!internalValue.value.location &&
      internalValue.value.location.locationId === undefined;
    const isChanged = !isEqual(newVal.location, location.value.location);
    shouldValidate.value = isAdhocLocation && isChanged;
  },
  { deep: true, immediate: true },
);

watch(
  isVisible,
  (newVal) => {
    if (newVal) {
      const Clazz = Object.getPrototypeOf(location.value).constructor;
      internalValue.value = new Clazz(location.value);

      if (internalValue.value.location.locationId) {
        loadDoesUserHavePermission(internalValue.value.location.locationId);
      }

      v$.value.$reset();
    }
  },
  { immediate: true },
);

watch(isShowingAddressBookModal, (_newVal: boolean) => {
  resetSearch();
});
</script>

<style>
.location-edit-modal.rygen-modal.p-dialog {
  --p-dialog-color: var(--p-gray-600);
  --p-dialog-title-font-weight: 500;

  border: 0;

  .p-dialog-header {
    border-left-style: solid;
    border-left-width: var(--sp-1);
    border-left-color: var(--stop-type-accent-color);
    border-radius: var(--p-border-radius-sm) var(--p-border-radius-sm) 0 0;

    .modal-header {
      width: 100%;

      .location-edit-modal-header {
        display: flex;
        gap: var(--gap-1);
        width: 100%;

        .rygen-button {
          flex: 0 0 auto;
        }

        .location-edit-modal-header-label {
          display: flex;
          gap: var(--gap-1);
          align-items: center;
          flex: 1 0 auto;

          .fa-layers {
            font-size: 21px;
          }
        }

        .location-edit-modal-header-address {
          display: flex;
          gap: var(--gap-1);
          justify-content: space-between;
          align-items: center;
          width: 100%;
          /* Keeps this div from overflowing */
          min-width: 0;

          .header-left {
            display: flex;
            gap: var(--gap-1);
            align-items: center;
            /* Keeps this div from overflowing */
            min-width: 0;

            .location-name {
              font-weight: 400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }
    }
  }

  .p-dialog-content {
    display: flex;
    flex-flow: column;
    padding: 0;

    .location-edit-modal-body {
      padding: var(--sp-6) var(--sp-12);
    }
  }
}
</style>
